import { gql, useMutation } from '@apollo/client';
import { Button, Text, TextField } from '@radix-ui/themes';
import { Form } from 'radix-ui';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { toast } from 'sonner';

type SubmitData = {
  firstName: string;
  lastName: string;
  email: string;
};

const INVITE_EXTERNAL_TO_RESOURCE = gql`
  mutation InviteExternalToResource($email: String!, $firstName: String!, $lastName: String!, $resourceId: String!) {
    InviteExternalToResource(
      input: { email: $email, firstName: $firstName, lastName: $lastName, resourceId: $resourceId }
    )
  }
`;

const InviteExternalToJob = ({ refetching }: { refetching: () => void }) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const [inviteExternalToJob, { loading }] = useMutation(INVITE_EXTERNAL_TO_RESOURCE, {
    onCompleted() {
      // refetching();
    },
  });

  return (
    <Form.Root
      onSubmit={(formEvent) => {
        formEvent.preventDefault();

        const form = formEvent.currentTarget;
        const formData = Object.fromEntries(new FormData(form)) as unknown as SubmitData;
        const invitee = {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim().toLowerCase(),
        };

        inviteExternalToJob({
          variables: {
            ...invitee,
            resourceId: id,
          },
          onCompleted: () => {
            toast.success(t('invite.toast.success', { firstName: invitee.firstName, lastName: invitee.lastName }));
          },
        }).finally(() => {
          refetching();
          form.reset();
        });
      }}
    >
      <Form.Field name="email" className="mb-1">
        <Text size="1" color="red" className="flex justify-end">
          <Form.Message match="valueMissing">{t('invite.form.emailRequired')}</Form.Message>
          <Form.Message match="typeMismatch">{t('invite.form.emailInvalid')}</Form.Message>
        </Text>
        <div className="flex items-center">
          <Form.Label asChild>
            <Text size="4" className="w-1/4">
              {t('invite.form.emailLabel')}
            </Text>
          </Form.Label>
          <Form.Control asChild>
            <TextField.Root
              placeholder={t('invite.form.emailPlaceholder')}
              type="email"
              required
              className="w-3/4"
              size="3"
            />
          </Form.Control>
        </div>
      </Form.Field>
      <Form.Field name="firstName" className="mb-1">
        <Text size="1" color="red" className="flex justify-end">
          <Form.Message match="valueMissing">{t('invite.form.firstNameRequired')}</Form.Message>
        </Text>
        <div className="flex items-center">
          <Form.Label asChild>
            <Text size="4" className="w-1/4">
              {t('invite.form.firstNameLabel')}
            </Text>
          </Form.Label>
          <Form.Control asChild>
            <TextField.Root placeholder={t('invite.form.firstNamePlaceholder')} required className="w-3/4" size="3" />
          </Form.Control>
        </div>
      </Form.Field>
      <Form.Field name="lastName" className="mb-1">
        <Text size="1" color="red" className="flex justify-end">
          <Form.Message match="valueMissing">{t('invite.form.lastNameRequired')}</Form.Message>
        </Text>
        <div className="flex items-center">
          <Form.Label asChild>
            <Text size="4" className="w-1/4">
              {t('invite.form.lastNameLabel')}
            </Text>
          </Form.Label>
          <Form.Control asChild>
            <TextField.Root placeholder={t('invite.form.lastNamePlaceholder')} required className="w-3/4" size="3" />
          </Form.Control>
        </div>
      </Form.Field>
      <Form.Submit asChild>
        <Button size="3" className="w-full!" loading={loading}>
          {t('invite.submitButton')}
        </Button>
      </Form.Submit>
    </Form.Root>
  );
};

export default InviteExternalToJob;
